import {
  Box,
  Button,
  Card,
  Flex,
  InfoBox,
  Select,
  Stack,
  Text,
  Textarea,
  notify,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdContentCopy, MdDownload } from "react-icons/md";
import { customerCompanyService } from "services/customerCompanyService";
import { NotificationInboundSetup } from "services/notificationService";
import { getThemeColor } from "theme/colors";

/**
 * The two-way side of the Telegram, Slack and Teams channels, shared by the
 * tenant-wide Notifications page and a customer company's Integrations tab:
 *
 * - InboundSetupSection — a channel's Slack app manifest and request URLs, its
 *   Telegram webhook, or its Teams messaging endpoint and app package, shown in
 *   the channel's editor.
 * - ChatApprovalLimitCard — one customer company's ceiling on what its members
 *   may approve with a chat-app button (staff parity with the portal setting).
 *
 * Hiding a button is only UX: the server re-checks identity and the ceiling on
 * every click, whatever the message showed.
 */

const I18N = "app.customerCompanies.integrations.chatApps";

const CopyButton = ({ text }: { text: string }) => {
  const { t } = useTranslation();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      notify({
        type: "success",
        title: t(`${I18N}.copied`, { defaultValue: "Copied" }),
      });
    } catch {
      notify({
        type: "error",
        title: t("app.errors.requestFailed", {
          defaultValue: "Request failed",
        }),
      });
    }
  };

  return (
    <Button intent="ghost" size="sm" onClick={() => void copy()}>
      <MdContentCopy /> {t(`${I18N}.copy`, { defaultValue: "Copy" })}
    </Button>
  );
};

const UrlRow = ({ label, url }: { label: string; url: string }) => (
  <Flex justify="space-between" align="center" gap={2}>
    <Box minW={0}>
      <Text fontSize="xs" color="var(--text-secondary-color)">
        {label}
      </Text>
      <Text fontSize="xs" fontFamily="mono" wordBreak="break-all">
        {url}
      </Text>
    </Box>
    <CopyButton text={url} />
  </Flex>
);

interface ChatAppChannel {
  providerType: string;
  inboundSetup?: NotificationInboundSetup | null;
  teamsSettings?: { appId?: string | null } | null;
}

/**
 * The Azure Bot steps, the messaging endpoint, the chat link and the app
 * package of a Teams channel. A Teams channel cannot exist without a public
 * URL, so a missing endpoint means the server lost it after the channel was made.
 */
const TeamsSetup = ({
  setup,
  hasAppId,
  onDownloadPackage,
}: {
  setup?: NotificationInboundSetup | null;
  hasAppId: boolean;
  onDownloadPackage?: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);

  if (!setup?.teamsMessagingEndpoint) {
    return (
      <InfoBox variant="warning">
        <Text fontSize="xs">
          {t(`${I18N}.teamsOff`, {
            defaultValue:
              "Microsoft Teams is off on this server: it has no public URL, so Teams cannot reach it.",
          })}
        </Text>
      </InfoBox>
    );
  }

  const download = async () => {
    if (!onDownloadPackage) return;
    setDownloading(true);
    try {
      await onDownloadPackage();
    } catch (err: any) {
      notify({
        type: "error",
        title: t(`${I18N}.teamsPackageFailed`, {
          defaultValue: "Could not download the Teams app package.",
        }),
        description: err.response?.data?.error,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <InfoBox variant="info">
      <Stack gap={2}>
        <Text fontSize="sm" fontWeight="medium">
          {t(`${I18N}.teamsTitle`, {
            defaultValue: "Connect the Teams bot",
          })}
        </Text>
        <Box as="ol" pl={5} fontSize="xs" listStyleType="decimal">
          <li>
            {t(`${I18N}.teamsStep1`, {
              defaultValue:
                "In the Azure portal, create an Azure Bot: type Single Tenant, with a new Microsoft App ID.",
            })}
          </li>
          <li>
            {t(`${I18N}.teamsStep2`, {
              defaultValue:
                "In the bot's app registration, add a client secret. Paste the App ID, the secret and the Directory (tenant) ID below, then save.",
            })}
          </li>
          <li>
            {t(`${I18N}.teamsStep3`, {
              defaultValue:
                "In the Azure Bot, set Configuration → Messaging endpoint to the URL below, then add the Microsoft Teams channel.",
            })}
          </li>
          <li>
            {t(`${I18N}.teamsStep4`, {
              defaultValue:
                "Download the app package and upload it in the Teams admin center → Teams apps → Manage apps, or send it to your Teams admin.",
            })}
          </li>
          <li>
            {t(`${I18N}.teamsStep5`, {
              defaultValue:
                "Each person then connects Microsoft Teams in their notification settings.",
            })}
          </li>
        </Box>
        <UrlRow
          label={t(`${I18N}.teamsEndpoint`, {
            defaultValue: "Messaging endpoint",
          })}
          url={setup.teamsMessagingEndpoint}
        />
        {setup.teamsChatLink && (
          <UrlRow
            label={t(`${I18N}.teamsChatLink`, {
              defaultValue:
                "Chat with the bot (works once the app is installed)",
            })}
            url={setup.teamsChatLink}
          />
        )}
        <Flex gap={2} align="center" wrap="wrap">
          <Button
            intent="secondary"
            size="sm"
            onClick={() => void download()}
            disabled={!hasAppId || !onDownloadPackage || downloading}
          >
            <MdDownload />{" "}
            {t(`${I18N}.teamsPackage`, {
              defaultValue: "Download app package",
            })}
          </Button>
          {!hasAppId && (
            <Text fontSize="xs">
              {t(`${I18N}.teamsPackageNeedsAppId`, {
                defaultValue: "Save the App ID first.",
              })}
            </Text>
          )}
        </Flex>
        <Text fontSize="xs" color="var(--text-secondary-color)">
          {t(`${I18N}.teamsSingleTenant`, {
            defaultValue:
              "One organisation only: the bot answers people in the Directory (tenant) ID below and ignores everyone else.",
          })}
        </Text>
      </Stack>
    </InfoBox>
  );
};

/** How an existing Telegram, Slack or Teams channel takes clicks, replies and commands. */
export const InboundSetupSection = ({
  channel,
  onDownloadTeamsPackage,
}: {
  channel: ChatAppChannel;
  /** Downloads the channel's Teams app package from the caller's scope. */
  onDownloadTeamsPackage?: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const setup = channel.inboundSetup;

  if (channel.providerType === "Teams") {
    return (
      <TeamsSetup
        setup={setup}
        hasAppId={Boolean(channel.teamsSettings?.appId)}
        onDownloadPackage={onDownloadTeamsPackage}
      />
    );
  }

  if (channel.providerType === "Telegram") {
    return (
      <InfoBox variant="info">
        <Stack gap={2}>
          <Text fontSize="sm" fontWeight="medium">
            {t(`${I18N}.title`, {
              defaultValue: "Buttons, replies and commands",
            })}
          </Text>
          {setup?.telegramWebhookUrl ? (
            <>
              <Text fontSize="xs">
                {t(`${I18N}.telegramOn`, {
                  defaultValue:
                    "On. The bot's webhook and command menu are set up when this channel is saved or tested.",
                })}
              </Text>
              <UrlRow
                label={t(`${I18N}.webhookUrl`, { defaultValue: "Webhook URL" })}
                url={setup.telegramWebhookUrl}
              />
            </>
          ) : (
            <Text fontSize="xs">
              {t(`${I18N}.off`, {
                defaultValue:
                  "Off on this server — messages carry an Open link only.",
              })}
            </Text>
          )}
        </Stack>
      </InfoBox>
    );
  }

  if (channel.providerType !== "Slack" || !setup?.slackManifest) return null;

  return (
    <InfoBox variant="info">
      <Stack gap={2}>
        <Flex justify="space-between" align="center" gap={2}>
          <Text fontSize="sm" fontWeight="medium">
            {t(`${I18N}.manifestTitle`, {
              defaultValue: "Create the Slack app from this manifest",
            })}
          </Text>
          <CopyButton text={setup.slackManifest} />
        </Flex>
        <Box as="ol" pl={5} fontSize="xs" listStyleType="decimal">
          <li>
            {t(`${I18N}.manifestStep1`, {
              defaultValue: "Copy the manifest below.",
            })}
          </li>
          <li>
            {t(`${I18N}.manifestStep2`, {
              defaultValue:
                "At api.slack.com/apps, choose Create New App → From a manifest, pick the workspace and paste it.",
            })}
          </li>
          <li>
            {t(`${I18N}.manifestStep3`, {
              defaultValue: "Install the app to the workspace.",
            })}
          </li>
          <li>
            {t(`${I18N}.manifestStep4`, {
              defaultValue:
                "Paste the Bot User OAuth Token and the Signing Secret (Basic Information) below, then save.",
            })}
          </li>
        </Box>
        <Textarea
          readOnly
          value={setup.slackManifest}
          rows={8}
          fontFamily="mono"
          fontSize="xs"
        />
        {setup.slackEventsUrl ? (
          <Stack gap={2}>
            <Text fontSize="xs" fontWeight="medium">
              {t(`${I18N}.urlsTitle`, {
                defaultValue: "Request URLs (already in the manifest)",
              })}
            </Text>
            <UrlRow
              label={t(`${I18N}.eventsUrl`, {
                defaultValue: "Event Subscriptions",
              })}
              url={setup.slackEventsUrl}
            />
            {setup.slackInteractionsUrl && (
              <UrlRow
                label={t(`${I18N}.interactionsUrl`, {
                  defaultValue: "Interactivity",
                })}
                url={setup.slackInteractionsUrl}
              />
            )}
            {setup.slackCommandsUrl && (
              <UrlRow
                label={t(`${I18N}.commandsUrl`, {
                  defaultValue: "Slash commands",
                })}
                url={setup.slackCommandsUrl}
              />
            )}
          </Stack>
        ) : (
          <Text fontSize="xs">
            {t(`${I18N}.sendOnly`, {
              defaultValue:
                "Buttons, replies and commands are off on this server, so this manifest creates a send-only app.",
            })}
          </Text>
        )}
      </Stack>
    </InfoBox>
  );
};

/** Select value for a stored ceiling; "high" allows everything, like no limit. */
const selectValueOf = (ceiling?: string | null) =>
  !ceiling || ceiling === "high" ? "" : ceiling;

/** One customer company's limit on what may be approved with a Slack, Telegram or Teams button. */
export const ChatApprovalLimitCard = ({
  customerCompanyId,
}: {
  customerCompanyId: string;
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setValue(null);
    setLoadFailed(false);
    customerCompanyService
      .getById(customerCompanyId)
      .then((company) => {
        if (!cancelled) setValue(selectValueOf(company.chatApprovalCeiling));
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [customerCompanyId]);

  const save = async (next: string) => {
    const previous = value;
    setValue(next);
    setSaving(true);
    try {
      const company = await customerCompanyService.update(customerCompanyId, {
        chatApprovalCeiling: next,
      });
      setValue(selectValueOf(company.chatApprovalCeiling));
      notify({
        type: "success",
        title: t(`${I18N}.limitSaved`, {
          defaultValue: "Approval limit saved.",
        }),
      });
    } catch (err: any) {
      setValue(previous);
      notify({
        type: "error",
        title: t(`${I18N}.limitSaveFailed`, {
          defaultValue: "Could not save the approval limit.",
        }),
        description: err.response?.data?.error,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card.Root
      borderWidth="1px"
      borderColor={getThemeColor("card.border")}
      borderRadius="md"
    >
      <Card.Body>
        <Stack gap={3}>
          <Stack gap={1}>
            <Text fontWeight="medium">
              {t(`${I18N}.limitTitle`, {
                defaultValue: "Approvals from chat apps",
              })}
            </Text>
            <Text fontSize="sm" color="var(--text-secondary-color)">
              {t(`${I18N}.limitHint`, {
                defaultValue:
                  "The highest risk this company's members may approve with a button in Slack, Telegram or Microsoft Teams. Above it, messages show only Deny and Open, and the approval is made in the app.",
              })}
            </Text>
          </Stack>
          {loadFailed ? (
            <Text fontSize="sm" color="red.500">
              {t(`${I18N}.limitLoadFailed`, {
                defaultValue: "Could not load the approval limit.",
              })}
            </Text>
          ) : (
            <Select
              label={t(`${I18N}.limitLabel`, {
                defaultValue: "Allowed from chat apps",
              })}
              items={[
                {
                  label: t(`${I18N}.limitAll`, {
                    defaultValue: "Everything (default)",
                  }),
                  value: "",
                },
                {
                  label: t(`${I18N}.limitMedium`, {
                    defaultValue: "Up to medium risk",
                  }),
                  value: "medium",
                },
                {
                  label: t(`${I18N}.limitLow`, {
                    defaultValue: "Low risk only",
                  }),
                  value: "low",
                },
                {
                  label: t(`${I18N}.limitNone`, {
                    defaultValue: "Deny only",
                  }),
                  value: "none",
                },
              ]}
              value={[value ?? ""]}
              onValueChange={({ value: picked }) => void save(picked[0] ?? "")}
              disabled={value === null || saving}
            />
          )}
        </Stack>
      </Card.Body>
    </Card.Root>
  );
};
